import api from './api';

// Types aligned with backend pg_wallet
interface WalletData {
  id: number;
  user_id: number;
  entity_id: number;
  saldo: number;
  saldo_bonus: number;
  saldo_bloqueado: number;
  saldo_disponivel: number;
  moeda: string;
  status: string;
  nivel_verificacao: string;
  limite_diario: number;
  limite_transacao: number;
  limite_mensal: number;
  total_carregado: number;
  total_gasto: number;
  total_transferido: number;
  total_transacoes: number;
}

interface WalletStats {
  saldo: number;
  saldo_bonus: number;
  saldo_disponivel: number;
  total_carregado: number;
  total_gasto: number;
  total_transferido: number;
  total_topups: number;
  total_pagamentos: number;
  total_enviado: number;
  total_recebido: number;
  total_transacoes: number;
  nivel_verificacao: string;
  limites: {
    diario: number;
    transacao: number;
    mensal: number;
  };
}

interface WalletTransaction {
  id: number;
  tipo: string;
  valor: number;
  valor_liquido: number;
  saldo_anterior: number;
  saldo_posterior: number;
  descricao: string;
  referencia: string;
  contraparte_nome?: string;
  contraparte_tipo?: string;
  categoria?: string;
  metodo_pagamento?: string;
  status: string;
  created_at: string;
}

interface WalletBalance {
  saldo: number;
  saldo_bonus: number;
  saldo_bloqueado: number;
  saldo_disponivel: number;
  moeda: string;
  status: string;
}

interface TopupResult {
  pagamento_id: number;
  referencia: string;
  valor: number;
  metodo?: string;
  status: string;
  expira_em?: string;
  // Redirect SISP (produção — transactionCode=1)
  paymentUrl?: string;
  merchantRef?: string;
  // ATM reference (legacy)
  sisp?: {
    entidade: string;
    referencia: string;
    valor: number;
  };
}

interface TransferResult {
  referencia: string;
  valor: number;
  destinatario: string;
  saldo_anterior: number;
  saldo_posterior: number;
}

interface PayResult {
  referencia: string;
  valor: number;
  saldo_anterior: number;
  saldo_posterior: number;
}

class WalletService {
  /**
   * GET /client/wallet — Get wallet data
   */
  async getWallet(): Promise<WalletData | null> {
    const response = await api.get<{ success: boolean; data?: WalletData | null; error?: string }>(
      '/client/wallet'
    );
    if (response.data.success) {
      return response.data.data || null;
    }
    throw new Error(response.data.error || 'Falha ao carregar wallet');
  }

  /**
   * GET /client/wallet/balance — Quick balance check
   */
  async getBalance(): Promise<WalletBalance> {
    try {
      const response = await api.get<{ success: boolean; data?: WalletBalance; error?: string }>(
        '/client/wallet/balance'
      );
      if (response.data.success && response.data.data) {
        return response.data.data;
      }
    } catch { /* fallback below */ }

    // Fallback: try /wallet/:userId
    try {
      const auth = sessionStorage.getItem('auth');
      if (auth) {
        const parsed = JSON.parse(auth);
        const userId = parsed.user?.mysqlId || parsed.user?.id || parsed.cliente?.USER_ID;
        if (userId) {
          const response = await api.get<{ success: boolean; data?: any }>(`/wallet/${userId}`);
          if (response.data.success && response.data.data) {
            const w = response.data.data;
            return {
              saldo: parseFloat(w.saldo) || 0,
              saldo_bonus: parseFloat(w.saldo_bonus) || 0,
              saldo_bloqueado: parseFloat(w.saldo_bloqueado) || 0,
              saldo_disponivel: parseFloat(w.saldo_disponivel || w.saldo) || 0,
              moeda: w.moeda || 'CVE',
              status: w.status || 'ACTIVE',
            };
          }
        }
      }
    } catch { /* ignore */ }

    return { saldo: 0, saldo_bonus: 0, saldo_bloqueado: 0, saldo_disponivel: 0, moeda: 'CVE', status: 'ACTIVE' };
  }

  /**
   * GET /client/wallet/stats — Wallet statistics
   */
  async getStats(): Promise<WalletStats> {
    const response = await api.get<{ success: boolean; data?: WalletStats; error?: string }>(
      '/client/wallet/stats'
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao carregar estatísticas');
  }

  /**
   * GET /client/wallet/transactions — Transaction history
   */
  async getTransactions(params?: {
    page?: number;
    limit?: number;
    tipo?: string;
    status?: string;
    from?: string;
    to?: string;
  }): Promise<{ data: WalletTransaction[]; pagination: any }> {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', String(params.page));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.tipo) queryParams.append('tipo', params.tipo);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.from) queryParams.append('from', params.from);
    if (params?.to) queryParams.append('to', params.to);

    const query = queryParams.toString();
    const url = `/client/wallet/transactions${query ? `?${query}` : ''}`;

    const response = await api.get<{
      success: boolean;
      data?: WalletTransaction[];
      pagination?: any;
      error?: string;
    }>(url);

    if (response.data.success) {
      return {
        data: response.data.data || [],
        pagination: response.data.pagination || {},
      };
    }
    throw new Error(response.data.error || 'Falha ao carregar transações');
  }

  // ── Flow de cotação (Pagali Pass) ────────────────────────────────────────

  async createQuote(valor: number, instrument = 'VINTI4', contractId?: string): Promise<{
    quoteId: string; grossAmountCVE: number; feeCVE: number; feeVatCVE: number;
    netCreditedCVE: number; appliedRateBps: number; disclosureText: string; expiresAt: string;
  }> {
    const response = await api.post<{ success: boolean; quoteId?: string; grossAmountCVE?: number;
      feeCVE?: number; feeVatCVE?: number; netCreditedCVE?: number; appliedRateBps?: number;
      disclosureText?: string; expiresAt?: string; error?: string }>(
      '/client/wallet/topup/quote',
      { valor, instrument, ...(contractId ? { contractId } : {}) }
    );
    if (response.data.success && response.data.quoteId) return response.data as any;
    throw new Error(response.data.error || 'Erro ao criar cotação');
  }

  async markQuoteDisplayed(quoteId: string): Promise<void> {
    await api.patch(`/client/wallet/topup/quote/${quoteId}/displayed`);
  }

  async acceptQuote(quoteId: string): Promise<void> {
    await api.patch(`/client/wallet/topup/quote/${quoteId}/accepted`);
  }

  async executeTopUp(quoteId: string, idempotencyKey: string): Promise<{ saldo: number; transactionId: string }> {
    const response = await api.post<{
      success: boolean; transactionId?: string; saldo?: number;
      data?: { saldo: number; transactionId: string }; error?: string;
    }>(
      '/client/wallet/topup/execute',
      { quoteId, idempotencyKey },
    );
    if (response.data.success) {
      const saldo = response.data.saldo ?? response.data.data?.saldo ?? 0;
      const transactionId = response.data.transactionId ?? response.data.data?.transactionId ?? '';
      return { saldo, transactionId };
    }
    throw new Error(response.data.error || 'Erro ao executar carregamento');
  }

  /** @deprecated Substituído pelo flow de cotação. Mantido só para compatibilidade. */
  async topup(valor: number, metodo?: string, tokenId?: string): Promise<TopupResult> {
    const response = await api.post<{ success: boolean; data?: TopupResult; paymentUrl?: string; error?: string }>(
      '/client/wallet/topup',
      { valor, metodo: metodo || 'VINTI4', tokenId }
    );
    if (response.data.success) {
      return response.data.data ?? ({ paymentUrl: response.data.paymentUrl } as TopupResult);
    }
    throw new Error(response.data.error || 'Falha ao iniciar carregamento');
  }

  /**
   * POST /client/wallet/transfer — Transfer to another user
   */
  async transfer(params: {
    destino_email?: string;
    destino_telefone?: string;
    destino_user_id?: string;
    valor: number;
    descricao?: string;
    pin?: string;
  }): Promise<TransferResult> {
    const response = await api.post<{ success: boolean; data?: TransferResult; error?: string }>(
      '/client/wallet/transfer',
      params
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao transferir');
  }

  /**
   * POST /client/wallet/pay — Pay with wallet (saldo interno)
   */
  async pay(params: {
    valor: number;
    destino_tipo?: string;
    destino_id?: string;
    descricao?: string;
    categoria?: string;
    pin?: string;
  }): Promise<PayResult> {
    const response = await api.post<{ success: boolean; data?: PayResult; error?: string }>(
      '/client/wallet/pay',
      params
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao pagar');
  }

  /**
   * POST /client/wallet/pay-with-token — Pay with saved card (SISP token)
   */
  async payWithToken(params: {
    tokenId?: string;
    valor: number;
    descricao?: string;
    destino_tipo?: string;
    destino_id?: string;
    categoria?: string;
    pin?: string;
  }): Promise<PayResult> {
    const response = await api.post<{ success: boolean; data?: PayResult; error?: string }>(
      '/client/wallet/pay-with-token',
      params
    );
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao pagar com cartão');
  }

  /**
   * POST /client/wallet/pin — Set or change PIN
   */
  async setPin(novo_pin: string, pin_atual?: string): Promise<void> {
    const response = await api.post<{ success: boolean; error?: string }>(
      '/client/wallet/pin',
      { novo_pin, pin_atual }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao configurar PIN');
    }
  }

  // ─── SISP Card/Token Management ───

  /**
   * GET /client/wallet/cards — List saved payment cards
   */
  async getCards(): Promise<SavedCard[]> {
    const response = await api.get<{ success: boolean; data?: SavedCard[]; error?: string }>(
      '/client/wallet/cards'
    );
    if (response.data.success) {
      return response.data.data || [];
    }
    throw new Error(response.data.error || 'Falha ao carregar cartões');
  }

  /**
   * POST /client/wallet/cards — Initiate card tokenization (add card)
   */
  async addCard(params?: {
    maxTransactions?: number;
    maxAmount?: number;
    returnUrl?: string;
  }): Promise<{ redirectUrl: string; id: string; merchantRef: string }> {
    const response = await api.post<{
      success: boolean;
      data?: { redirectUrl: string; id: string; merchantRef: string };
      error?: string;
    }>('/client/wallet/cards', params || {});
    if (response.data.success && response.data.data) {
      return response.data.data;
    }
    throw new Error(response.data.error || 'Falha ao iniciar adição de cartão');
  }

  /**
   * DELETE /client/wallet/cards/:id — Remove saved card
   */
  async removeCard(tokenId: string): Promise<void> {
    const response = await api.delete<{ success: boolean; error?: string }>(
      `/client/wallet/cards/${tokenId}`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao remover cartão');
    }
  }

  /**
   * PUT /client/wallet/cards/:id/default — Set card as default
   */
  async setDefaultCard(tokenId: string): Promise<void> {
    const response = await api.put<{ success: boolean; error?: string }>(
      `/client/wallet/cards/${tokenId}/default`
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao definir cartão padrão');
    }
  }

  /**
   * PUT /client/wallet/cards/:id/alias — Update card alias
   */
  async updateCardAlias(tokenId: string, alias: string): Promise<void> {
    const response = await api.put<{ success: boolean; error?: string }>(
      `/client/wallet/cards/${tokenId}/alias`,
      { alias }
    );
    if (!response.data.success) {
      throw new Error(response.data.error || 'Falha ao atualizar alias');
    }
  }
}

// SISP saved card type (camelCase from backend getClientTokens)
export interface SavedCard {
  id: string;
  token: string;
  lastFourDigits: string;
  cardType: string;
  cardHolder: string;
  expireMonth: string;
  expireYear: string;
  isDefault: string;
  alias: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  // Limites SISP (MOP023 v1.1)
  maxTransactions?: number | null;
  maxAmount?: number | null;
  limitDate?: string | null;
  // Uso actual (calculado a partir dos TOKEN_PAYMENTs)
  usedTransactions?: number;
  usedAmount?: number;
}

export default new WalletService();
